import * as React from "react";

import { cn } from "./cn";

type BreakdownColor = "chart-1" | "chart-2" | "chart-3" | "chart-4" | "chart-5";

type BreakdownItem = {
  key: string;
  label: string;
  /** Pre-formatted amount. */
  value: string;
  /** Share of the whole, 0..100. */
  pct: number;
  href?: string;
  onSelect?: () => void;
};

type BreakdownListProps = {
  ariaLabel: string;
  items: BreakdownItem[];
  color?: BreakdownColor;
  /** Rows shown before a "Show all N" button. */
  limit?: number;
  emptyLabel?: string;
  className?: string;
};

/** "59%", "<1%" for a share above zero but under one, "0%". */
function formatShare(pct: number): string {
  if (pct > 0 && pct < 1) return "<1%";
  return `${Math.round(pct)}%`;
}

const rowClass = "block w-full text-left";
const interactiveRow =
  "rounded-control focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring hover:bg-muted";

function RowBody({
  item,
  color,
}: {
  item: BreakdownItem;
  color: BreakdownColor;
}) {
  const width = Math.min(100, Math.max(0, item.pct));
  return (
    <>
      <span className="flex items-baseline gap-3">
        <span
          className="type-small w-0 min-w-0 flex-1 truncate text-foreground"
          title={item.label}
        >
          {item.label}
        </span>
        <span className="type-figure type-small shrink-0 text-foreground">
          {item.value}
        </span>
        <span className="type-figure w-9 shrink-0 text-right text-xs text-muted-foreground">
          {formatShare(item.pct)}
        </span>
      </span>
      <span
        aria-hidden="true"
        className="mt-1 block h-1 overflow-hidden rounded-pill bg-muted"
      >
        <span
          data-slot="breakdown-fill"
          className="block h-full rounded-pill opacity-40"
          style={{
            width: `${width}%`,
            backgroundColor: `var(--nt-${color})`,
          }}
        />
      </span>
    </>
  );
}

function BreakdownList({
  ariaLabel,
  items,
  color = "chart-1",
  limit,
  emptyLabel,
  className,
}: BreakdownListProps) {
  const [expanded, setExpanded] = React.useState(false);
  if (items.length === 0) {
    return emptyLabel ? (
      <p className={cn("type-small text-muted-foreground", className)}>
        {emptyLabel}
      </p>
    ) : null;
  }
  const capped = limit !== undefined && items.length > limit;
  const shown = capped && !expanded ? items.slice(0, limit) : items;
  return (
    <div className={className}>
      <ul aria-label={ariaLabel} className="flex flex-col gap-3">
        {shown.map((item) => (
          <li key={item.key}>
            {item.href ? (
              <a
                href={item.href}
                className={cn(rowClass, interactiveRow, "-mx-1 px-1 py-0.5")}
              >
                <RowBody item={item} color={color} />
              </a>
            ) : item.onSelect ? (
              <button
                type="button"
                onClick={item.onSelect}
                className={cn(rowClass, interactiveRow, "-mx-1 px-1 py-0.5")}
              >
                <RowBody item={item} color={color} />
              </button>
            ) : (
              <div>
                <RowBody item={item} color={color} />
              </div>
            )}
          </li>
        ))}
      </ul>
      {capped ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="type-small mt-3 rounded-control font-medium text-foreground underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {expanded ? "Show fewer" : `Show all ${items.length}`}
        </button>
      ) : null}
    </div>
  );
}

export { BreakdownList, formatShare };
export type { BreakdownColor, BreakdownItem, BreakdownListProps };
